import { getReportContractorRecommendations } from '@/api'
import axios from '../../AxiosService'
import errorReporter from '@/plugins/errorReporterPlugin'
import uniqueArray from '@/utils/uniqueArray'

const genericError = 'An error has occurred. Your changes likely did not save.'
const loadingError = "We're having trouble loading data. Please try again."

const firebaseProject = process.env.FIREBASE_PROJECT || 'spectora-x'
const firebaseFunctionRegion =
  process.env.FIREBASE_FUNCTION_REGION || 'us-central1'
const cloudFunctionUrl = `https://${firebaseFunctionRegion}-${firebaseProject}.cloudfunctions.net`
export const cloudFunction = (name, data) =>
  axios.post(`${cloudFunctionUrl}/${name}`, { data })

export const closeOverlay = ({ commit, dispatch, state }) => {
  commit('setValue', { key: 'overlayPhoto', value: null })
}

export const fetchAccount = ({ commit, dispatch, state }) => {
  return dispatch('fetchUserProfile').then((profile) => {
    if (
      profile.attributes &&
      profile.type !== 'admin' &&
      profile.attributes.company_id
    )
      return dispatch('fetchCompany', profile.attributes.company_id)
  })
}

export const createSampleReport = ({ commit, dispatch, state }, params) => {
  return new Promise((resolve, reject) => {
    axios
      .post('/api/v1/sample_reports', params)
      .then((response) => {
        dispatch(
          'notifier/show',
          { type: 'Saved', msg: 'Sample Report Created!' },
          { root: true }
        )
        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', genericError, { root: true })
        errorReporter.error('Error creating Sample Report', error)
        reject(error)
      })
  })
}

export const fetchCompany = ({ commit, dispatch, state }, companyId) => {
  return new Promise((resolve, reject) => {
    let prefix =
      state.loggedIn && state.userProfile.attributes.company_id ? '' : 'public/'
    axios
      .get(`/api/v1/${prefix}companies/` + companyId)
      .then((response) => {
        commit('setValue', { key: 'company', value: response.data.data })
        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error('Error fetching company', error)
        reject(error)
      })
  })
}

export const fetchInspection = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    let url =
      state.loggedIn && !state.sampleReport
        ? `inspections/${state.report.attributes.inspection_id}?include=inspection_attachments,buyer,reports,assignments`
        : `public/reports/${state.report.id}/inspections/${state.report.attributes.inspection_id}?include=inspectors,inspection_attachments,reports,inspection_reviews,assignments`
    axios
      .get(`/api/v1/${url}`)
      .then((response) => {
        let insp = response.data.data
        let incs = response.data.included || []

        incs.forEach((inc) => {
          let key = inc.type + 's'
          if (!insp[key]) insp[key] = []
          insp[key].push(inc)
        })

        commit('setValue', { key: 'inspection', value: insp })
        resolve(insp)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error('Error fetching inspection', error)
        reject(error)
      })
  })
}

export const fetchObservations = ({ commit, dispatch, state }, params = {}) => {
  return new Promise((resolve, reject) => {
    let url = state.loggedIn
      ? 'observations'
      : `public/reports/${state.report.id}/observations`
    let items = state.items.reduce((summ, i) => {
      summ[i.id] = {
        id: i.id,
        name: i.attributes.name.toLowerCase(),
        priority: i.attributes.priority,
      }
      return summ
    }, {})

    axios
      .get(`/api/v1/${url}?` + axios.objectToQuery(params))
      .then((response) => {
        let obs = response.data.data.sort((a, b) => {
          let aItem = items[a.attributes.report_item_id]
          let bItem = items[b.attributes.report_item_id]

          if (aItem.id === bItem.id) {
            if (a.attributes.priority === b.attributes.priority) {
              if (
                (a.attributes.name || '').toLowerCase() >
                (b.attributes.name || '').toLowerCase()
              ) {
                return 1
              } else {
                return -1
              }
            } else {
              return a.attributes.priority - b.attributes.priority
            }
          } else {
            if (aItem.priority === bItem.priority) {
              if (
                (aItem.name || '').toLowerCase() >
                (bItem.name || '').toLowerCase()
              ) {
                return 1
              } else {
                return -1
              }
            } else {
              return aItem.priority - bItem.priority
            }
          }
        })
        obs.forEach((o) => {
          if (o.relationships) {
            let rels = (o.relationships.observation_photos || {}).data || []
            let relIds = rels.map((r) => {
              return r.id
            })
            o.attributes.observation_photos = response.data.included.filter(
              (op) => {
                return relIds.includes(op.id)
              }
            )
            o.attributes.observation_photos.forEach((op) => {
              if (op.attributes.priority === null) op.attributes.priority = 100
            })
            o.attributes.observation_photos.sort((a, b) => {
              return (a.attributes.priority || 0) - (b.attributes.priority || 0)
            })
          }
        })

        commit('setValue', {
          key: 'observations',
          value: state.observations.concat(obs),
        })
        resolve(obs)
      })
  })
}

export const fetchPdfStatus = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    axios.get(`/api/v1/public/reports/${state.report.id}`).then((response) => {
      let report = response.data.data

      if (!report.attributes.pdf_started_at) {
        state.report.attributes.pdf_started_at = null
        state.report.attributes.pdf_url = report.attributes.pdf_url
        state.report.attributes.summary_url = report.attributes.summary_url
        dispatch(
          'notifier/show',
          { type: 'Saved', msg: 'Your PDFs have been refreshed!' },
          { root: true }
        )
      }
    })
  })
}

export const fetchRecommendations = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    const companyId = state.inspection.attributes.company_id
    axios
      .get(
        `/api/v1/public/recommendations?reporting_format=true&company_id=${companyId}`
      )
      .then((response) => {
        commit('setValue', {
          key: 'recommendations',
          value: response.data.data,
        })
        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error('Error loading recommendations', error)
        reject(error)
      })
  })
}

export const fetchReportContractors = async (
  { commit, dispatch, state },
  reportId
) => {
  const resp = await getReportContractorRecommendations({
    report_id: reportId,
  })

  console.log('[fetchReportContractors] resp, ', resp)

  if (resp.data) {
    const presentContractorTypes = uniqueArray(
      state.observations
        .filter(
          (observation) =>
            !['limit', 'info'].includes(observation.attributes.comment_type)
        )
        .map((observation) => observation.attributes.recommendation_id)
        .filter((item) => item != null)
    )

    let recommendedContractors = []
    let recommendedContractorTypes = []

    const contractors = resp.data.data
    if (contractors) {
      // builds a unique list of contractor types based on what was returned in the contractor results
      // ['roofing', 'plumber', 'etc'...]
      contractors.forEach((contractor) => {
        const contractorTypes =
          (contractor &&
            contractor.attributes &&
            contractor.attributes.contractor_types) ||
          []

        contractorTypes.forEach((type) => {
          if (!recommendedContractorTypes.includes(type)) {
            recommendedContractorTypes.push(type)
          }
        })
      })

      // returns a sorted list of contractor types
      recommendedContractorTypes = uniqueArray(
        recommendedContractorTypes.sort((a, b) => (a > b ? 1 : -1))
      )

      // builds an array of contractor recommendations grouped by type
      recommendedContractors = recommendedContractorTypes
        .map((type) => {
          const filteredContractors = contractors.filter((contractor) =>
            (contractor.attributes.contractor_types || []).includes(type)
          )

          // ensure we're only adding contractors that have recommendations specified
          if (!presentContractorTypes.includes(type)) {
            return null
          }

          return {
            id: type,
            name: type,
            contractors: filteredContractors,
          }
        })
        .filter((item) => item != null)

      // ensure that the sidebar can access recommended contractors to show the menu item if any are present
      commit('setValue', {
        key: 'recommendedContractors',
        value: recommendedContractors,
      })
    }
    commit('setValue', { key: 'reportContractors', value: resp.data.data })
  }
}

export const fetchReportFirestore = async (
  { commit, dispatch, state },
  reportId
) => {
  let response
  try {
    console.log(
      `fetching report: ${process.env.FIRESTORE_PROCESSED_MEDIA}/${reportId}.json?alt=media`
    )
    response = await axios.get(
      `${process.env.FIRESTORE_PROCESSED_MEDIA}/${reportId}.json?alt=media`
    )
  } catch (error) {
    return false
  }
  const attributify = (collection) =>
    collection.map((attributes) => ({ id: attributes.id, attributes }))
  const report = response.data.report
  const stats = response.data.report.stats
  const reportView = response.data.report_view
  // Data mapping
  report.sections.map((section) => {
    section.items.map((item) => {
      item.observations.map((observation) => {
        observation.photos.map((photo) => {
          if (photo.video_url) {
            photo.video = true
          }
          if (!photo.video_url) {
            photo.image = photo.edited_image_url || photo.original_image_url
          }
          photo.report_section_id = section.id
          photo.report_item_id = item.id
          photo.observation_id = observation.id
        })
        observation.report_section_id = section.id
        observation.report_item_id = item.id
      })
      item.report_section_id = section.id
    })
  })

  const sections = report.sections
  const items = sections.map((x) => x.items).flat()
  const observations = items
    .map((item) => {
      item.observations.map((observation) => {
        observation.observation_photos = attributify(observation.photos)
      })
      return item.observations
    })
    .flat()
  commit('setValue', {
    key: 'report',
    value: {
      ...report,
      firestore: true,
      attributes: {
        ...report,
        image_url: report.image,
        settings: report,
        stats,
      },
    },
  })

  const defects = attributify(
    observations.filter((observation) => observation.comment_type === 'defect')
  )
  const defectsBySection = defects.reduce((acc, curr) => {
    const section = sections.find(
      ({ id }) => id === curr.attributes.report_section_id
    )
    if (!acc[section.name]) {
      acc[section.name] = {
        defects: [],
      }
    }

    acc[section.name].defects.push(curr)
    return acc
  }, {})

  commit('setValue', { key: 'sections', value: attributify(report.sections) })
  commit('setValue', { key: 'items', value: attributify(items) })
  commit('setValue', { key: 'observations', value: attributify(observations) })
  commit('setValue', {
    key: 'defects',
    value: defects,
  })
  commit('setValue', {
    key: 'defectsBySection',
    value: defectsBySection,
  })
  commit('setValue', {
    key: 'inspection',
    value: {
      attributes: {
        property_address: report.inspection.address1,
        property_address_2: report.inspection.address2,
      },
    },
  })
  commit('setValue', {
    key: 'inspector',
    value: {
      attributes: {
        property_address: report.inspection.address1,
        property_address_2: report.inspection.address2,
      },
    },
  })
  if (reportView) {
    commit('setValue', { key: 'reportView', value: reportView })
  }
  return true
}

export const updateReportPayload = async (
  { commit, dispatch, state },
  reportId
) => {
  let hasUpdates = false
  try {
    const res = await cloudFunction('checkForReportUpdates', { id: reportId })
    hasUpdates = res.data.result
  } catch (error) {
    return false
  }
  if (!hasUpdates) {
    return false
  }
  await cloudFunction('compileReport', { id: reportId })
  return true
}

export const fetchReport = ({ commit, dispatch, state }, reportId) => {
  return new Promise((resolve, reject) => {
    let prefix = state.loggedIn ? '' : 'public/'
    axios
      .get(
        `/api/v1/${prefix}reports/${reportId}?view=stats,not_show_recommended_contractors_on_reports&include=repair_request_views,report_assignments`
      )
      .then((response) => {
        commit('setValue', { key: 'report', value: response.data.data })
        commit('setValue', {
          key: 'reportViews',
          value: (response.data.included || []).filter(
            (i) => i.type === 'report_view'
          ),
        })
        commit('setValue', {
          key: 'reportAssignments',
          value: (response.data.included || [])
            .filter((i) => i.type === 'report_assignment')
            .sort((a, b) => {
              if (a.attributes.primary) {
                return -1
              } else if (b.attributes.primary) {
                return 1
              } else {
                return a.attributes.priority - b.attributes.priority
              }
            }),
        })

        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error('Error loading report', error)
        reject(error)
      })
  })
}

export const fetchReportView = ({ commit, dispatch, state }, reportViewId) => {
  let params = {}
  if (reportViewId === state.report.attributes.settings.report_view_id) {
    params.view = 'observations,observation_photos'
    params.include = 'report'
  }

  return new Promise((resolve, reject) => {
    axios
      .get(
        `/api/v1/public/reports/${state.report.id}/report_views/${reportViewId}`,
        { params: params }
      )
      .then((response) => {
        commit('setValue', { key: 'reportView', value: response.data.data })
        if (response.data.included)
          commit('setValue', {
            key: 'originalReport',
            value: response.data.included[0],
          })
        resolve(response.data.data)
      })
      .catch((error) => {
        commit(
          'notifier/showError',
          "We're having trouble loading your Repair Request",
          { root: true }
        )
        errorReporter.error('Error loading report view', error)
        reject(error)
      })
  })
}

export const fetchSampleReport = (
  { commit, dispatch, state },
  sampleReportId
) => {
  return new Promise((resolve, reject) => {
    axios
      .get(`/api/v1/sample_reports/${sampleReportId}`)
      .then((response) => {
        commit('setValue', { key: 'sampleReport', value: response.data.data })
        return resolve(
          dispatch('fetchReport', response.data.data.attributes.report_id)
        )
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error('Error loading sample report', error)
        reject(error)
      })
  })
}

export const fetchSections = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    let url = state.loggedIn
      ? `report_sections?report_id=${state.report.id}&include=report_items`
      : `public/reports/${state.report.id}/report_sections?include=report_items`

    axios
      .get(`/api/v1/${url}`)
      .then((response) => {
        let sections = response.data.data
        let items = response.data.included
        commit('setValue', { key: 'sections', value: sections })
        commit('setValue', { key: 'items', value: items })
        resolve(sections)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error('Error loading report sections', error)
        reject(error)
      })
  })
}

export const fetchUserProfile = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    axios
      .get('/api/v1/user_profile')
      .then((response) => {
        commit('setValue', { key: 'userProfile', value: response.data.data })
        commit('setValue', { key: 'loggedIn', value: true })
        resolve(response.data.data)
      })
      .catch((error) => {
        console.log(error)
        commit('setValue', { key: 'loggedIn', value: false })
        resolve(true)
      })
  })
}

export const fetchViewer = ({ commit, dispatch, state }, idToken) => {
  axios
    .get(`/api/v1/public/reports/${state.report.id}/profiles/${idToken}`)
    .then((response) => {
      commit('setValue', { key: 'viewer', value: response.data.data })
    })
}

export const reportViewUpdateSelection = (
  { commit, dispatch, state },
  observation
) => {
  let dupeSelections = JSON.parse(JSON.stringify(state.reportViewObservations))
  if (
    state.reportViewObservations.find((o) => {
      return o.observation_id === observation.observation_id
    })
  ) {
    dupeSelections = dupeSelections.map((o) => {
      return o.observation_id === observation.observation_id ? observation : o
    })
  } else {
    dupeSelections.push(observation)
  }

  commit('setValue', { key: 'reportViewObservations', value: dupeSelections })
}

export const submitReport = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    axios
      .put(`/api/v1/reports/${state.report.id}`, {
        attributes: { id: state.report.id, submitted_on: new Date() },
      })
      .then((response) => {
        dispatch(
          'notifier/show',
          { type: 'Saved', msg: 'This report has been submitted!' },
          { root: true }
        )
        resolve(response.data.data)
      })
      .catch((error) => {
        commit(
          'notifier/show',
          { type: 'Error', msg: 'There was an error submitting this report.' },
          { root: true }
        )
        errorReporter.error('Error submitting report', error)
        reject(error)
      })
  })
}

export const updatePdfs = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    axios
      .put(`/api/v1/reports/${state.report.id}/update_pdf`)
      .then((response) => {
        dispatch(
          'notifier/show',
          {
            type: 'Saved',
            msg: "Your PDFs are being refreshed! They'll be available again shortly.",
          },
          { root: true }
        )
        let updateReport = JSON.parse(JSON.stringify(state.report))
        updateReport.attributes.pdf_started_at =
          response.data.data.attributes.pdf_started_at
        updateReport.attributes.summary_pdf_started_at =
          response.data.data.attributes.summary_pdf_started_at

        commit('setValue', { key: 'report', value: updateReport })
      })
      .catch((error) => {
        commit('notifier/showError', 'There was an error refreshing your PDFs')
        errorReporter.error('Error refreshing pdfs', error)
        reject(error)
      })
  })
}

export const removeObservation = (
  { commit, dispatch, state },
  observationId
) => {
  let obs = state.observations
  obs = obs.filter((o) => o.id !== observationId)
  commit('setValue', { key: 'observations', value: obs })
  dispatch('setDefectCounts')
}

export const setDefectCounts = ({ commit, dispatch, state }) => {
  let dc = {
    full: {},
    summary: {},
    hazards: {},
    all: {},
    rh: {},
    resolved: [],
    notResolved: [],
    unsatisfactory: [],
  }
  state.sections.forEach((s) => {
    Object.keys(dc).forEach((k) => {
      dc[k][s.id] = []
    })
  })

  let summaryCategories = []
  if (state.report.attributes.settings.category_high_in_summary)
    summaryCategories.push(1)
  if (state.report.attributes.settings.category_low_in_summary)
    summaryCategories.push(-1)
  if (state.report.attributes.settings.category_med_in_summary)
    summaryCategories.push(0)

  state.observations.forEach((o) => {
    if (o.attributes.comment_type === 'defect') {
      let item = state.items.find((i) => {
        return i.id === o.attributes.report_item_id
      })
      if (item) {
        dc.full[item.attributes.report_section_id].push(o.id)
        dc.all[item.attributes.report_section_id].push(o.id)
        if (summaryCategories.includes(o.attributes.category))
          dc.summary[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 1)
          dc.hazards[item.attributes.report_section_id].push(o.id)
        if ([1, 0].includes(o.attributes.category))
          dc.rh[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 2)
          dc.resolved[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 3)
          dc.unsatisfactory[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 4)
          dc.notResolved[item.attributes.report_section_id].push(o.id)
      }
    }
  })

  commit('setValue', { key: 'defectCounts', value: dc })
}
